import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Edit, RefreshCw, X, Loader2, Users, User, Building, TreePine } from 'lucide-react';

const ZG_ICONS = {
  waldbesitzer: User,
  fbg: Users,
  kommune: Building,
  dienstleister: TreePine,
};

export function EingabeKontext({ freitext, zielgruppe, onFreitextAendern, onZielgruppeAendern, onNeuAnalyse, loading, t, zielgruppen }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(freitext);
  const [showZg, setShowZg] = useState(false);

  const ZgIcon = ZG_ICONS[zielgruppe?.key] || User;

  const handleSave = () => {
    if (editText.trim() && editText.trim() !== freitext) {
      onFreitextAendern(editText.trim());
    }
    setEditing(false);
  };

  return (
    <Card className="border-warm-200 bg-warm-50/50 shadow-none" data-testid="eingabe-kontext">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-muted-foreground">{t('fa.kontextEingabe')}</p>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="text-[10px] px-1.5 py-0 border-primary/20 text-primary/70 cursor-pointer hover:border-primary/40"
              onClick={() => setShowZg(!showZg)}
              data-testid="zielgruppe-badge"
            >
              <ZgIcon className="h-2.5 w-2.5 mr-1" />
              {zielgruppe?.label}
            </Badge>
            {!editing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setEditText(freitext); setEditing(true); }}
                className="h-6 px-2 text-xs text-muted-foreground hover:text-primary"
                data-testid="kontext-bearbeiten-btn"
              >
                <Edit className="h-3 w-3 mr-1" />
                {t('fa.kontextBearbeiten')}
              </Button>
            )}
          </div>
        </div>

        {showZg && zielgruppen?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3 p-2.5 bg-white rounded-md border border-warm-200" data-testid="zielgruppen-auswahl">
            <p className="w-full text-[10px] text-muted-foreground mb-1">{t('fa.kontextZgAendern')}</p>
            {zielgruppen.map((zg) => {
              const Icon = ZG_ICONS[zg.key] || User;
              return (
                <button
                  key={zg.key}
                  onClick={() => { onZielgruppeAendern?.(zg); setShowZg(false); }}
                  className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border transition-all ${zg.key === zielgruppe?.key ? 'bg-primary text-white border-primary' : 'bg-white border-warm-200 text-foreground hover:border-primary/40'}`}
                  data-testid={`zg-option-${zg.key}`}
                >
                  <Icon className="h-3 w-3" />
                  {zg.label}
                </button>
              );
            })}
          </div>
        )}

        {editing ? (
          <div className="space-y-2" data-testid="kontext-edit-bereich">
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="min-h-[80px] text-sm resize-none"
              data-testid="kontext-edit-input"
            />
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} data-testid="kontext-abbrechen-btn">
                <X className="h-3 w-3 mr-1" /> {t('fa.kontextAbbrechen')}
              </Button>
              <Button size="sm" onClick={handleSave} className="flex-1" data-testid="kontext-korrigieren-btn">
                <Edit className="h-3 w-3 mr-1" /> {t('fa.kontextKorrigieren')}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-foreground leading-relaxed line-clamp-3" data-testid="kontext-freitext">
            {freitext}
          </p>
        )}

        {!editing && onNeuAnalyse && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onNeuAnalyse}
            disabled={loading}
            className="mt-2 h-7 px-2 text-xs text-muted-foreground hover:text-primary"
            data-testid="kontext-neu-analyse-btn"
          >
            {loading ? (
              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> {t('fa.kontextAnalysiert')}</>
            ) : (
              <><RefreshCw className="h-3 w-3 mr-1" /> {t('fa.kontextErneutAnalysieren')}</>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
